import { defaultFeatures } from '../features';
import type {
  ClientInitialState,
  HeaderConfigResponse,
  HeaderStateResponse,
  NavigationCategories,
} from './types';

const fetchApi = async <TResponse extends Record<string, unknown>>(
  url: string,
  options?: RequestInit,
  fallback: TResponse = {} as TResponse
): Promise<TResponse> => {
  try {
    const response = await window.fetch(url, options);
    return (await response.json()) as TResponse;
  } catch (err) {
    console.error(`Header: error fetching initial data from ${url}: ${err}`);
    return fallback;
  }
};

const getYesterday = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return date.toISOString().slice(0, 10);
};

const fetchFallbackCategories = async (): Promise<NavigationCategories> => {
  const cacheFilename = `/header-ui-assets/cache/fallback-cache-${getYesterday()}.json`;
  const response = await fetchApi<NavigationCategories>(cacheFilename);
  return response.child ? response : { child: [] };
};

export const getInitialData = async (
  isMin1024pxWide = false,
  params: URLSearchParams = new URLSearchParams(),
  exp = '',
  includeParams = ''
): Promise<ClientInitialState> => {
  const configUrl = '/header/api/config';

  const stateResponse = {
    ...(defaultFeatures as HeaderStateResponse),
    headerClientConfigQueryParameters: includeParams,
  };

  const queryParams = stateResponse?.headerClientConfigQueryParameters || '';
  const configUrlParams = new URLSearchParams(queryParams);

  if (isMin1024pxWide) {
    configUrlParams.append('isdesktop', 'true');
  }

  const date = params.get('date');
  if (date) {
    configUrlParams.append('date', date);
    const time = params.get('time');
    if (time) {
      configUrlParams.append('time', time);
    }
  }

  if (exp) {
    configUrlParams.append('exp', exp);
  }

  const encodedParams = configUrlParams.toString();
  const url = `${configUrl}${encodedParams.length ? `?${encodedParams}` : ''}`;

  const configResponse = await fetchApi<HeaderConfigResponse>(url);

  const categories =
    configResponse.categories || (await fetchFallbackCategories());

  return {
    ...configResponse,
    ...stateResponse,
    categories,
  };
};
